import React, { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";

import { createTextTransform } from "../../../actions/text-transform/createTextTransform";
import Dropzone from "../../../components/forms/GenericDropzone";
import UploadedFilesList from "../../../components/lists/UploadedFilesList";
import PdfPasswordModal from "../../../components/modals/PdfPasswordModal";
import InlineProcessingStatus from "../../../components/processing/InlineProcessingStatus";
import TransformPanel from "../../../components/transform/TransformPanel";
import { useUploadForm } from "../../../hooks/useUploadForm";
import { useTransform } from "../../../hooks/useTransform";
import TextTransformList from "../../text-transform/TextTransformList";
import { scribePrimaryCtaContainerSx } from "../../../constants/scribeUi";
import GlassPanel from "../GlassPanel";

const DocumentTab: React.FC<{ isApproved: boolean }> = ({ isApproved }) => {
  const uploadForm = useUploadForm({ minFiles: 1, maxFiles: 1, maxTotalSizeMB: 60 });
  const transform = useTransform();
  const [downloadTarget, setDownloadTarget] = useState("");
  const [originalDownloadTarget, setOriginalDownloadTarget] = useState("");

  useEffect(() => {
    if (transform.transformDownloadKey) {
      setDownloadTarget(transform.transformDownloadKey);
    } else if (originalDownloadTarget) {
      setDownloadTarget(originalDownloadTarget);
    }
  }, [transform.transformDownloadKey, originalDownloadTarget]);

  const uploadHandler = uploadForm.createApiUploadHandler(
    async (fileKeys, filePasswords) =>
      createTextTransform(fileKeys, {
        sourceText: "",
        transformType: "improve",
        customInstruction: "Extract and return the full text content of this document exactly as-is. Do not summarize, rephrase, or alter the text in any way.",
        additionalContext: "",
        filePasswords,
        enableDocumentChat: false,
      }),
    () => {},
  );

  const handleSubmit = (e: React.FormEvent) => uploadForm.handleSubmit(e);

  return (
    <>
      <Typography
        sx={{
          fontFamily: "'Syne', sans-serif",
          fontSize: "0.8rem",
          fontWeight: 700,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: "var(--teal)",
          mb: 1,
        }}
      >
        Document Extraction
      </Typography>

      <GlassPanel>
        <Box sx={{ opacity: uploadForm.processing ? 0.65 : 1, pointerEvents: uploadForm.processing ? "none" : "auto" }}>
        <form onSubmit={handleSubmit}>
          <Dropzone
            onDrop={uploadForm.createDropHandler(isApproved)}
            multiple={false}
            mainText="Upload Document"
            subText="PDF or DOCX — scanned PDFs processed with OCR"
            buttonText="Select Document (.pdf, .docx)"
          />
          <UploadedFilesList
            title="Selected Document"
            files={uploadForm.files}
            onRemoveFile={uploadForm.removeFile}
            filePasswords={uploadForm.filePasswords}
          />
          {uploadForm.fileError && (
            <Typography color="error" sx={{ mt: 1, fontSize: "0.82rem" }}>
              {uploadForm.fileError}
            </Typography>
          )}

          <Box sx={[{ mt: 3 }, scribePrimaryCtaContainerSx]}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={!isApproved || uploadForm.files.length !== 1 || uploadForm.processing}
            >
              📄 Extract to Word
            </Button>
          </Box>
        </form>
        </Box>
        <InlineProcessingStatus
          open={uploadForm.showLoader}
          files={uploadForm.files}
          jobId={uploadForm.jobId}
          userId={uploadForm.userId}
          onUpload={uploadHandler}
          onComplete={async () => uploadForm.setProcessing(false)}
          onTextExtracted={transform.streamSourceText}
          onDownloadReady={(key: string) => { setDownloadTarget(key); setOriginalDownloadTarget(key); }}
          onClose={uploadForm.handleModalClose}
          documentTitle="Document Extraction"
        />
        <TransformPanel
          displayText={transform.displayText}
          hasText={transform.hasText}
          isTransforming={transform.isTransforming}
          isStreamingResult={transform.isStreamingResult}
          isSourceStreaming={transform.isSourceStreaming}
          transformError={transform.transformError}
          downloadUrl={downloadTarget}
          inlineChatJobId={uploadForm.jobId}
          inlineChatFileName={uploadForm.files[0]?.name || "Document Extraction"}
          onTransform={transform.runTransform}
          onReset={transform.reset}
        />
      </GlassPanel>
      <PdfPasswordModal
        open={uploadForm.showPasswordModal}
        file={uploadForm.currentFileNeedingPassword}
        onSubmit={uploadForm.handlePasswordSubmit}
        onSkip={uploadForm.handlePasswordSkip}
        onCancel={uploadForm.handlePasswordCancel}
      />

      <Box sx={{ mt: 3 }}>
        <Typography
          sx={{
            fontFamily: "'Syne', sans-serif",
            fontSize: "0.8rem",
            fontWeight: 700,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "var(--teal)",
            mb: 1,
          }}
        >
          Recent Documents
        </Typography>
        <GlassPanel>
          <TextTransformList
            refresh={uploadForm.refresh}
            onRefreshComplete={uploadForm.handleRefreshComplete}
          />
        </GlassPanel>
      </Box>
    </>
  );
};

export default DocumentTab;
